import Component from "../../lib/component";
import {XHRFetcher} from "../../lib/xhr_fetcher";
import {executeScriptsInNode} from "../../helpers/scripts";
import {OnboardingConfig} from "./onboarding";
import {hasSeenOnboarding, markOnboardingSeen} from "./onboarding_seen_flag";
import {HOST_VIEW_CLOSE_EVENT} from "../../constants/host_view_events";

export const OnlineAppOnboardingConfig = {
    componentTypeKey: 'OnlineAppOnboarding',
    baseHtmlClass: 'online_app__onboarding',
    selector: '.online_app__onboarding',

    datasetConfigKey: 'online_app_onboarding__config',

    visibleHtmlClassModifier: 'visible',
    closedHtmlClassModifier: 'closed',

    closeTransitionDuration: 500, // Keep in step with the transition in _online_app__onboarding.scss.
};

/**
 * The web app's host for the one-time welcome carousel — the counterpart of the native apps'
 * onboarding webview. It owns the surface (the seen-flag, showing, closing); the carousel inside it
 * (see onboarding.js) owns the content, and asks to be closed via the generic `HostView-close`
 * signal rather than knowing anything about this host.
 *
 * The carousel's markup is fetched on demand rather than rendered into the page, because the page
 * is cached and shared by everyone: a traveller who has already seen the carousel would otherwise
 * download its artwork on every visit, forever.
 */
export class OnlineAppOnboarding extends Component {

    static get config() {
        return OnlineAppOnboardingConfig;
    }

    constructor(id_or_element) {
        super({
            element: id_or_element,
            config: OnlineAppOnboarding.config
        });

        this.setDatasetConfig(OnlineAppOnboardingConfig.datasetConfigKey);

        this.storageKey = this.getDatasetConfigValue('storage_key');
        this.onboardingPath = this.getDatasetConfigValue('onboarding_path');

        this.isVisible = false;
        this.isClosed = false;
        this.closeTimeoutId = null;
        this.onDestroy(() => window.clearTimeout(this.closeTimeoutId));

        if (hasSeenOnboarding(this.storageKey)) {
            this.removeOverlay();
            return;
        }

        this.listenForClose();
        this.load();
    }

    render() {
        this.conditionallySetHtmlClassState(this.isVisible, OnlineAppOnboardingConfig.visibleHtmlClassModifier);
        this.conditionallySetHtmlClassState(this.isClosed, OnlineAppOnboardingConfig.closedHtmlClassModifier);
    }

    // The same fragment the native apps load as a whole page (the `snip` format of
    // Online::AppsController#onboarding). Its stylesheet and bundle come with it — the scripts have
    // to be re-created to run, as parsed-in scripts never execute.
    async load() {
        if (!this.onboardingPath) return;
        try {
            const html = await new XHRFetcher(this.onboardingPath).fetch();
            const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
            const onboardingElement = parsedDocument.querySelector(OnboardingConfig.selector);
            if (!onboardingElement) throw new Error('No onboarding content in the fetched fragment');

            this.element.appendChild(document.importNode(onboardingElement, true));
            await this.awaitStylesheets(this.element);
            executeScriptsInNode(this.element);

            this.isVisible = true;
            this.render();
        } catch (error) {
            // A welcome is never worth breaking the app for — fail quiet and leave them on the map.
            console.warn('[OnlineAppOnboarding] Could not load the onboarding carousel', error);
            this.removeOverlay();
        }
    }

    // The fragment brings its own stylesheet AND its own bundle, and for a web client both are
    // links/src — two independent async loads with no ordering between them. Without this wait the
    // carousel can construct against UNSTYLED content, where its slides have non-zero but intrinsic
    // heights; the layout gate in pane_slide.js cannot tell that apart from real layout, so this is
    // the web surface's half of the same defect. Waiting here also stops the overlay fading in over
    // unstyled content.
    //
    // Resolves on `error` as well as `load` — a stylesheet that will never arrive must not hold the
    // welcome forever. No timeout: a stalled load leaves the overlay at `opacity: 0` /
    // `pointer-events: none`, which is the same fail-quiet outcome as the catch below.
    // See plans/2026-08-06--pane-slides-first-slide-recognition.md
    awaitStylesheets(node) {
        const links = Array.from(node.querySelectorAll('link[rel="stylesheet"]'));
        return Promise.all(links.map(link => {
            if (link.sheet) return Promise.resolve(); // Already applied.
            return new Promise(resolve => {
                link.addEventListener('load', resolve, {once: true});
                link.addEventListener('error', resolve, {once: true});
            });
        }));
    }

    listenForClose() {
        const handler = () => this.close();
        document.addEventListener(HOST_VIEW_CLOSE_EVENT, handler);
        this.onDestroy(() => document.removeEventListener(HOST_VIEW_CLOSE_EVENT, handler));
    }

    close() {
        if (this.isClosed) return;
        this.isClosed = true;
        markOnboardingSeen(this.storageKey);
        this.render();
        // Registered for teardown like any timer that outlives the element. Safe to cancel: the
        // only thing that destroys this component is its element leaving the DOM, which is what
        // this timer was going to do anyway.
        this.closeTimeoutId = window.setTimeout(() => this.removeOverlay(), OnlineAppOnboardingConfig.closeTransitionDuration);
    }

    removeOverlay() {
        if (this.element.parentElement) this.element.parentElement.removeChild(this.element);
    }

}
